import type { ReactElement } from 'react';

import { useStackModal } from '@rango-dev/internal-profile/hooks';
import { Button, Flex, Heading, IconButton, Skeleton, Tooltip } from '@rango-dev/whale';
import { useWallets, useWidget } from '@rango-dev/widget-embedded';
import { Eye, EyeOff, Plus, Power, RotateCw } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { GTMEvents } from '../../../constants/events';
import { CONNECT_WALLETS_MODAL_ID } from '../../../constants/modals';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { requestLogOutUser } from '../../../state/ProfileSlice';
import { removeSeparator } from '../../../utils/Numbers';
import ConnectWalletModal from '../../ConnectWalletModal';
import { DisconnectAllWalletsModal } from '../DisconnectAllWalletsModal';
import styles from '../WalletDetails.module.css';

import { formatTotalBalance } from './WalletDetailsHeader.helpers';

const ICON_SIZE = 16;
const PROFILE_ROUTE = 'profile';
const BALANCE_SKELETON_WIDTH = '160px';
const BALANCE_SKELETON_HEIGHT = '40px';

export function WalletDetailsHeader(): ReactElement {
  const {
    wallets: { totalBalance, isLoading, refetch, details: walletsDetails },
  } = useWidget();
  const { push, clear } = useStackModal();

  const { disconnectAll } = useWallets();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector((state) => state.profile.isLoggedIn);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const sumWalletInUsd = removeSeparator(totalBalance);

  const [showTotalBalance, setShowTotalBalance] = useState(true);
  const [showDisconnectWalletsModal, setShowDisconnectWalletsModal] = useState(false);
  const toggleDisconnectWalletModal = () =>
    setShowDisconnectWalletsModal((prevState) => !prevState);

  const reloadWallet = () => {
    const accounts = walletsDetails.map(({ chain, address, walletType }) => ({
      chain,
      address,
      walletType,
    }));
    refetch(accounts || []);
  };

  /*
   * Disconnecting the wallets also signs the user out of their profile, so the app
   * goes back to its initial state.
   */
  const disconnect = async () => {
    void disconnectAll();
    clear();

    if (isLoggedIn) {
      await dispatch(requestLogOutUser());
      if (pathname.includes(PROFILE_ROUTE)) {
        navigate(`/${PROFILE_ROUTE}`, { replace: true });
      }
    }
  };

  const toggleTotalBalance = () => setShowTotalBalance((prevState) => !prevState);

  const handleShowConnectWalletsModal = () => {
    push({
      id: CONNECT_WALLETS_MODAL_ID,
      renderContent: (index) => (
        <ConnectWalletModal title={t('Connect Wallet')} indexInStack={index} />
      ),
    });
  };

  const handleReload = () => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: GTMEvents.RELOAD_BALANCE,
      });
    } catch {
      // do nothing
    }
    reloadWallet();
  };

  return (
    <>
      <DisconnectAllWalletsModal
        open={showDisconnectWalletsModal}
        onClose={toggleDisconnectWalletModal}
        onConfirm={() => void disconnect()}
      />
      <Flex
        justify="between"
        align="start"
        gap="3"
        p="4"
        position="relative"
        className={styles.panel}>
        <Flex direction="column" align="start" gap="4" minWidth="0">
          <Button
            id="wallet-details-modal-toggle-total-balance-icon-btn"
            size="1"
            variant="ghost"
            color="gray"
            radius="full"
            aria-label={showTotalBalance ? 'hide' : 'show'}
            onClick={toggleTotalBalance}>
            {t('Total value')}
            {showTotalBalance ? <Eye size={ICON_SIZE} /> : <EyeOff size={ICON_SIZE} />}
          </Button>
          <Flex align="center" gap={isLoading ? '5' : '2'} minWidth="0">
            {isLoading ? (
              <Skeleton width={BALANCE_SKELETON_WIDTH} height={BALANCE_SKELETON_HEIGHT} />
            ) : (
              <Heading size="8" truncate>
                {formatTotalBalance(showTotalBalance, sumWalletInUsd)}
              </Heading>
            )}
            <Tooltip content={t('Reload wallets')} className={styles.tooltip}>
              <IconButton
                id="wallet-details-modal-refresh-icon-btn"
                size="1"
                variant="ghost"
                color="gray"
                radius="full"
                aria-label="reload"
                disabled={isLoading}
                onClick={handleReload}>
                <RotateCw size={ICON_SIZE} />
              </IconButton>
            </Tooltip>
          </Flex>
        </Flex>

        <Flex align="start" gap="2" position="absolute" right="3">
          <Tooltip content={t('Connect another wallet')} className={styles.tooltip}>
            <Button
              id="wallet-details-modal-connect-more-wallet-icon-btn"
              size="2"
              variant="outline"
              radius="full"
              onClick={handleShowConnectWalletsModal}>
              <Plus size={ICON_SIZE} />
              {t('Add wallet')}
            </Button>
          </Tooltip>
          <Tooltip content={t('Disconnect all wallets')} className={styles.tooltip}>
            <IconButton
              id="wallet-details-modal-disconnect-icon-btn"
              size="2"
              variant="outline"
              radius="full"
              aria-label="disconnect"
              onClick={toggleDisconnectWalletModal}>
              <Power size={ICON_SIZE} />
            </IconButton>
          </Tooltip>
        </Flex>
      </Flex>
    </>
  );
}
